var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import classNames from 'classnames';
import RcDrawer from 'rc-drawer';
import * as React from 'react';
import { ConfigContext } from '../config-provider';
import { tuple } from '../_util/type';
const DrawerContext = React.createContext(null);
const PlacementTypes = tuple('top', 'right', 'bottom', 'left');
const SizeTypes = tuple('default', 'large');
const defaultPushState = { distance: 180 };
const Drawer = React.forwardRef((_a, ref) => {
    var { width, height, size = 'default', closable = true, placement = 'right', maskClosable = true, mask = true, level = null, keyboard = true, push = defaultPushState, closeIcon = <CloseOutlined />, bodyStyle, drawerStyle, className, visible: propsVisible, forceRender, children, zIndex, destroyOnClose, style, title, headerStyle, onClose, footer, footerStyle, prefixCls: customizePrefixCls, getContainer: customizeGetContainer, extra, afterVisibleChange } = _a, rest = __rest(_a, ["width", "height", "size", "closable", "placement", "maskClosable", "mask", "level", "keyboard", "push", "closeIcon", "bodyStyle", "drawerStyle", "className", "visible", "forceRender", "children", "zIndex", "destroyOnClose", "style", "title", "headerStyle", "onClose", "footer", "footerStyle", "prefixCls", "getContainer", "extra", "afterVisibleChange"]);
    const [internalPush, setPush] = React.useState(false);
    const parentDrawer = React.useContext(DrawerContext);
    const destroyCloseRef = React.useRef(false);
    const [load, setLoad] = React.useState(false);
    const [visible, setVisible] = React.useState(false);
    React.useEffect(() => {
        if (propsVisible) {
            setLoad(true);
        }
        else {
            setVisible(false);
        }
    }, [propsVisible]);
    React.useEffect(() => {
        if (load && propsVisible) {
            setVisible(true);
        }
    }, [load, propsVisible]);
    const { getPopupContainer, getPrefixCls, direction } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('drawer', customizePrefixCls);
    const getContainer = 
    // 有可能为 false，所以不能直接判断
    customizeGetContainer === undefined && getPopupContainer
        ? () => getPopupContainer(document.body)
        : customizeGetContainer;
    React.useEffect(() => {
        // fix: delete drawer in child and re-render, no push started.
        // <Drawer>{show && <Drawer />}</Drawer>
        if (propsVisible && parentDrawer) {
            parentDrawer.push();
        }
        return () => {
            if (parentDrawer) {
                parentDrawer.pull();
                // parentDrawer = null;
            }
        };
    }, []);
    React.useEffect(() => {
        if (parentDrawer) {
            if (visible) {
                parentDrawer.push();
            }
            else {
                parentDrawer.pull();
            }
        }
    }, [visible]);
    const operations = React.useMemo(() => ({
        push() {
            if (push) {
                setPush(true);
            }
        },
        pull() {
            if (push) {
                setPush(false);
            }
        },
    }), [push]);
    React.useImperativeHandle(ref, () => operations, [operations]);
    const getOffsetStyle = () => {
        // https://github.com/ant-design/ant-design/issues/24287
        if (!visible && !mask) {
            return {};
        }
        const offsetStyle = {};
        if (placement === 'left' || placement === 'right') {
            const defaultWidth = size === 'large' ? 736 : 378;
            offsetStyle.width = typeof width === 'undefined' ? defaultWidth : width;
        }
        else {
            const defaultHeight = size === 'large' ? 736 : 378;
            offsetStyle.height = typeof height === 'undefined' ? defaultHeight : height;
        }
        return offsetStyle;
    };
    const getRcDrawerStyle = () => {
        // get drawer push width or height
        const getPushTransform = (_placement) => {
            let distance;
            if (typeof push === 'boolean') {
                distance = push ? defaultPushState.distance : 0;
            }
            else {
                distance = push.distance;
            }
            distance = parseFloat(String(distance || 0));
            if (_placement === 'left' || _placement === 'right') {
                return `translateX(${_placement === 'left' ? distance : -distance}px)`;
            }
            if (_placement === 'top' || _placement === 'bottom') {
                return `translateY(${_placement === 'top' ? distance : -distance}px)`;
            }
        };
        // 当无 mask 时，将 width 应用到外层容器上
        // 解决 https://github.com/ant-design/ant-design/issues/12401 的问题
        const offsetStyle = mask ? {} : getOffsetStyle();
        return Object.assign(Object.assign({ zIndex, transform: internalPush ? getPushTransform(placement) : undefined }, offsetStyle), style);
    };
    const closeIconNode = closable && (<button type="button" onClick={onClose} aria-label="Close" className={`${prefixCls}-close`}>
        {closeIcon}
      </button>);
    function renderHeader() {
        if (!title && !closable) {
            return null;
        }
        return (<div className={classNames(`${prefixCls}-header`, {
                [`${prefixCls}-header-close-only`]: closable && !title && !extra,
            })} style={headerStyle}>
          <div className={`${prefixCls}-header-title`}>
            {closeIconNode}
            {title && <div className={`${prefixCls}-title`}>{title}</div>}
          </div>
          {extra && <div className={`${prefixCls}-extra`}>{extra}</div>}
        </div>);
    }
    function renderFooter() {
        if (!footer) {
            return null;
        }
        const footerClassName = `${prefixCls}-footer`;
        return (<div className={footerClassName} style={footerStyle}>
          {footer}
        </div>);
    }
    // render drawer body dom
    const renderBody = () => {
        // destroyCloseRef.current =false Load the body only once by default
        if (destroyCloseRef.current && !forceRender && !propsVisible) {
            return null;
        }
        return (<div className={`${prefixCls}-wrapper-body`} style={Object.assign({}, drawerStyle)}>
          {renderHeader()}
          <div className={`${prefixCls}-body`} style={bodyStyle}>
            {children}
          </div>
          {renderFooter()}
        </div>);
    };
    const drawerClassName = classNames({
        'no-mask': !mask,
        [`${prefixCls}-rtl`]: direction === 'rtl',
    }, className);
    const offsetStyle = mask ? getOffsetStyle() : {};
    return (<DrawerContext.Provider value={operations}>
        <RcDrawer handler={false} {...Object.assign({ placement,
        prefixCls,
        maskClosable,
        level,
        keyboard,
        children,
        onClose,
        forceRender }, rest)} {...offsetStyle} open={visible || propsVisible} showMask={mask} style={getRcDrawerStyle()} className={drawerClassName} getContainer={getContainer} afterVisibleChange={open => {
            if (open) {
                destroyCloseRef.current = false;
            }
            else if (destroyOnClose) {
                destroyCloseRef.current = true;
                setLoad(false);
            }
            afterVisibleChange === null || afterVisibleChange === void 0 ? void 0 : afterVisibleChange(open);
        }}>
          {renderBody()}
        </RcDrawer>
      </DrawerContext.Provider>);
});
Drawer.displayName = 'Drawer';
export default Drawer;
